import {
  Component,
  OnInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef, HostListener
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { InternalCalloutComponent } from '@vg-constellation/angular-19/internal-callout';
import { SpinnerModule } from '@vg-constellation/angular-19/spinner';
import { ButtonModule } from '@vg-constellation/angular-19/button';
import { IconModule } from '@vg-constellation/angular-19/icon';
import { StepIndicatorModule } from '@vg-constellation/angular-19/step-indicator';
import { HintErrorModule } from '@vg-constellation/angular-19/hint-error';
import { Store } from '@ngrx/store';
import { Observable, of } from 'rxjs';
import { catchError, map, take } from 'rxjs/operators';
import { Address, Addresses } from '../../../dto/address.interface';
import { setRecipient, setStepIndicatorIndex } from '../../../state/actions/actions';
import { selectStatementDetails, selectStepIndicatorIndex } from '../../../state/selectors/selector';
import { ConfirmService } from '../../../services/confirm/confirm.service';
import { CaasService } from '../../../services/caas/caas.service';
import { RecipientRadioListComponent } from '../../recipient-radio-list/recipient-radio-list.component';


interface RecipientData {
  addressLine1?: string;
  addressLine2?: string;
  addressLine3?: string;
  addressLine4?: string;
  city?: string;
  stateCode?: string;
  zipCode?: string;
  zipCodePlusFour?: string;
  foreign?: boolean;
  foreignAddressText?: string;
  countryCode?: string;
  countryName?: string;
}

interface Recipient {
  id: string;
  name: string;
  address: string;
  addressPurposeDescription?: string;
  data: RecipientData;
}

@Component({
  selector: 'app-send-confirmation',
  templateUrl: './send-confirmation.component.html',
  styleUrls: ['./send-confirmation.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, InternalCalloutComponent, SpinnerModule, ButtonModule, IconModule, StepIndicatorModule, HintErrorModule, RecipientRadioListComponent],
  standalone: true
})
export class SendConfirmationComponent implements OnInit {
  isSmallScreen: boolean;
  currentStepIndicatorIndex$!: Observable<number>;
  statementDetails$!: Observable<any>;
  recipients: Recipient[] = [];
  selectedRecipientKey = '';
  loading = false;
  errorMessage = '';
  content: any;

  constructor(
    private router: Router,
    private store: Store,
    private confirmService: ConfirmService,
    private caasService: CaasService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.checkScreenSize();
    window.scrollTo(0, 0);
    this.content = this.caasService.getPageContent()?.confirmationJourneyContent;

    this.currentStepIndicatorIndex$ = this.store.select(selectStepIndicatorIndex);
    this.statementDetails$ = this.store.select(selectStatementDetails);

    this.statementDetails$.pipe(take(1)).subscribe(details => {
      console.log('SendConfirmationComponent - received details:', details);
      if (!details?.confirmationId) {  // Changed from statementId to confirmationId
        console.log('No confirmationId found, redirecting to home');
        this.router.navigate(['/']);
        return;
      }
      console.log('confirmationId found, loading recipients');
      this.loadRecipients(details);
    });
  }
  @HostListener('window:resize', ['$event'])
  onResize() {
    this.checkScreenSize();
  }

  checkScreenSize() {
    this.isSmallScreen = window.innerWidth < 576; // Small screen size max width is 576px
  }

  private loadRecipients(details: any): void {
    this.loading = true;
    console.log("Printing the details object:", details);
    const isInterestedParty = details?.interestedParty ?? false;
    console.log('Loading recipients, isInterestedParty:', isInterestedParty);

    this.confirmService.getAddresses(isInterestedParty).pipe(
      take(1),
      map(response => this.transformRecipients(response)),
      catchError(err => {
        console.error('Error fetching addresses:', err);
        this.errorMessage = 'Unable to load recipient addresses. Please try again later.';
        this.loading = false;
        this.cdr.markForCheck();
        return of([]);
      })
    ).subscribe(recipients => {
      this.recipients = recipients;
      if (!recipients.length) {
        this.errorMessage = 'No recipient addresses found.';
      }
      this.loading = false;
      this.cdr.markForCheck();
    });
  }

  private transformRecipients(response: Addresses): Recipient[] {
    let rawAddresses: Address[] = [];
    if ((response.error?.length ?? 0) > 0) {
      console.error(response.error)
      return [];
    }

    rawAddresses = response.addresses || [];

    return rawAddresses.map((entry: Address, index: number): Recipient => {
      const addressLines = [
        entry.streetAddressLine1,
        entry.streetAddressLine2,
        entry.streetAddressLine3,
        entry.streetAddressLine4,
      ].filter(Boolean);

      const recipientName = entry.name || ''

      const addressParts = [
        ...addressLines,
        entry.city,
        entry.stateCode,
        ((entry.zipCode || '') + (entry.zipCodePlusFour ? '-' + entry.zipCodePlusFour : '')).trim(),
        entry.foreignAddressText || entry.countryName || ''
      ].filter(part => part?.trim()).join(', ');

      return {
        id: `recipient-${index}`,
        name: recipientName,
        address: addressParts,
        addressPurposeDescription: entry.addressPurposeDescription || '',
        data: {
          addressLine1: entry.streetAddressLine1 || '',
          addressLine2: entry.streetAddressLine2 || '',
          addressLine3: entry.streetAddressLine3 || '',
          addressLine4: entry.streetAddressLine4 || '',
          city: entry.city || '',
          stateCode: entry.stateCode || '',
          zipCode: entry.zipCode || '',
          zipCodePlusFour: entry.zipCodePlusFour || '',
          countryCode: entry.countryCode || '',
          foreign: !!entry.foreignAddressText,
          foreignAddressText: entry.foreignAddressText || '',
          countryName: entry.foreignAddressText || entry.countryName || ''
        }
      };
    });
  }

  onContinue(): void {
    const selectedRecipient = this.recipients.find(r =>
      `${r.id}-${r.name}-${r.address}` === this.selectedRecipientKey
    );

    if (!selectedRecipient) {
      this.errorMessage = 'Please select a recipient.';
      this.cdr.markForCheck();
      return;
    }

    const data = selectedRecipient.data;
    if (!data.addressLine1?.trim()) {
      this.errorMessage = 'Recipient address is missing AddressLine1.';
      this.cdr.markForCheck();
      return;
    }

    this.errorMessage = '';
    this.statementDetails$.pipe(take(1)).subscribe(details => {
      this.store.dispatch(setRecipient({
        confirmationId: details.confirmationId,
        confirmationDate: details.confirmationDate,
        description: details.description,
        name: selectedRecipient.name,
        address: selectedRecipient.address,
        addressLine1: data.addressLine1 || '',
        addressLine2: data.addressLine2 || '',
        addressLine3: data.addressLine3 || '',
        addressLine4: data.addressLine4 || '',
        city: data.city || '',
        stateCode: data.stateCode || '',
        zipCode: data.zipCode || '',
        zipCodePlusFour: data.zipCodePlusFour || '',
        foreign: data.foreign || false,
        foreignAddressText: data.foreignAddressText || '',
        countryCode: data.countryCode || '',
        countryName: data.countryName || ''
      }));
      this.store.dispatch(setStepIndicatorIndex({ stepIndicatorIndex: 2 }));
    });
  }

  onRecipientChange(): void {
    if (this.selectedRecipientKey) {
      this.errorMessage = '';
      this.cdr.markForCheck();
    }
  }

  onCancel(): void {
    this.router.navigate(['/']);
  }

  trackByRecipient(_index: number, recipient: Recipient): string {
    return recipient.id;
  }

  getStepText(stepNumber: number): string {
    const template = this.content?.stepProgressTemplate || 'Step ${step-number} of 3';
    return template.replace('${step-number}', String(stepNumber));
  }
}
